import React, { useContext, useEffect, useState, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import TextInput from '../../../../../../Form/TextInput';
import SidePopup from '../../../../SidePopup';

import { COUNTRY_OPTIONS } from '../../../../../../../constants/main';
import { UiContext } from '../../../../../../../context/UiContext';
import useSwitchableData from '../../../../../../../hooks/useSwitchableData';
import classes from './styles.module.scss';
import AdminService from '../../../../../../../services/AdminService';
import getResizedImageLogoUrl from '../../../../../../../helpers/getResizedImageLogoUrl';
import Textarea from '../../../../../../Form/Textarea';

export default function ViewCompany({
  isVisible,
  handleClose,
  companies,
  currentId,
  refetch,
  isOverlayed,
  showEdit,
}) {
  const { t } = useTranslation();

  const { currentDataIndex, switchToPrevDataElement, switchToNextDataElement } =
    useSwitchableData(companies, currentId);

  const { showModal, showUnknownErrorModal, setIsFetching, showConflictModal } =
    useContext(UiContext);

  const deleteCompany = () => {
    showModal({
      icon: 'delete',
      title: t(
        'dashboardComponents.Console.CompanyManagement.view.deleteConfirmationTitle'
      ),
      text: t(
        'dashboardComponents.Console.CompanyManagement.view.deleteConfirmationText'
      ),
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.deleteCompany({
            id: companies[currentDataIndex].id,
          });
          showModal({
            title: t(
              'dashboardComponents.Console.CompanyManagement.view.deleteSuccessTitle'
            ),
            text: t(
              'dashboardComponents.Console.CompanyManagement.view.deleteSuccessText'
            ),
            dismissButtonLabel: t('common.gotIt'),
            dismissButtonVariant: '',
          });
          handleClose();
        } catch (error) {
          if (error.response.status === 409) {
            showConflictModal(error.response.data.message);
          } else {
            showUnknownErrorModal();
          }
        } finally {
          await refetch();
          setIsFetching(false);
        }
      },
    });
  };

  const [locations, setLocations] = useState([]);

  const country = useMemo(
    () =>
      COUNTRY_OPTIONS.find(
        (c) => c.value === companies?.[currentDataIndex]?.country
      ),
    [companies, currentDataIndex]
  );

  const displayedCountry = country ? country.label : '';

  const managers = companies?.[currentDataIndex]?.managers || [];
  const [managersState, setManagersState] = useState(managers);

  const fetchLocations = async () => {
    try {
      const companyId = companies?.[currentDataIndex]?.id;
      if (!companyId) return;
      const data = await AdminService.getLocations({
        pageNumber: 1,
        pageSize: 100,
        Filters: `company.id:eq:${companyId}`,
      });
      setLocations(data?.data || data || []);
    } catch (error) {
      // silent
    }
  };

  const fetchManagers = async () => {
    try {
      const companyId = companies?.[currentDataIndex]?.id;
      if (!companyId) return;
      const page = await AdminService.getCompanies({
        pageNumber: 1,
        pageSize: 1,
        Filters: `id:eq:${companyId}`,
      });
      const updated = page?.data?.[0]?.managers || [];
      setManagersState(updated);
    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    if (isVisible && currentDataIndex !== null) {
      // Reset dependent data immediately to avoid showing stale content while switching
      setLocations([]);
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, currentDataIndex]);

  // When edit overlay closes, refresh locations
  useEffect(() => {
    if (!isOverlayed && isVisible && currentDataIndex !== null) {
      fetchLocations();
      fetchManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlayed]);

  useEffect(() => {
    setManagersState(managers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, currentDataIndex]);

  // Refresh managers list when the selected company changes while the popup is open
  useEffect(() => {
    if (isVisible && currentDataIndex !== null) {
      fetchManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDataIndex]);

  return (
    <SidePopup
      mode="view"
      leftButtonVariant="delete"
      isVisible={isVisible}
      handleClose={handleClose}
      title={t('dashboardComponents.Console.CompanyManagement.view.title')}
      leftButtonLabel={t('common.delete')}
      rightButtonLabel={t('common.edit')}
      switchPrev={companies?.length > 1 && switchToPrevDataElement}
      switchNext={companies?.length > 1 && switchToNextDataElement}
      onLeftButtonClick={deleteCompany}
      onRightButtonClick={() => showEdit(companies[currentDataIndex].id)}
      isOverlayed={isOverlayed}
      contentKey={companies?.[currentDataIndex]?.id}
    >
      <div className={classes.ViewCompany}>
        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.row}>
              <div className={classes.col}>
                <TextInput
                  label={t(
                    'dashboardComponents.Console.CompanyManagement.view.labels.Status'
                  )}
                  value={companies?.[currentDataIndex]?.status}
                  readonly
                  height={50}
                />
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.col}>
                <TextInput
                  label={t(
                    'dashboardComponents.Console.CompanyManagement.view.labels.Type'
                  )}
                  value={companies?.[currentDataIndex]?.type}
                  readonly
                  height={50}
                />
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.col}>
                <TextInput
                  label={t(
                    'dashboardComponents.Console.CompanyManagement.view.labels.Company Name'
                  )}
                  value={companies?.[currentDataIndex]?.name}
                  readonly
                  height={50}
                />
              </div>
            </div>
          </div>
          <div className={classes.col}>
            <div className={classes.logoSection}>
              <span>
                {t(
                  'dashboardComponents.Console.CompanyManagement.addEdit.labels.Logo'
                )}
              </span>
              <div className={classes.imageContainer}>
                {companies?.[currentDataIndex]?.logo ? (
                  <img
                    src={getResizedImageLogoUrl({
                      url: `company/${companies?.[currentDataIndex]?.logo}`,
                      width: 150,
                    })}
                    alt="Logo"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.view.labels.Industry'
              )}
              value={companies?.[currentDataIndex]?.industry?.name}
              readonly
              height={50}
            />
          </div>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.view.labels.Country'
              )}
              value={displayedCountry}
              readonly
              height={50}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.view.labels.Address'
              )}
              value={companies?.[currentDataIndex]?.address}
              readonly
              height={50}
            />
          </div>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.view.labels.VAT'
              )}
              readonly
              height={50}
              value={companies?.[currentDataIndex]?.vatId}
            />
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <Textarea
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Company Joining Note'
              )}
              value={companies?.[currentDataIndex]?.companyJoiningNote}
              height={100}
              name="companyJoiningNote"
              readonly
            />
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <Textarea
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Company Description'
              )}
              value={companies?.[currentDataIndex]?.companyDescription}
              height={100}
              name="companyDescription"
              readonly
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.managersHeader}>
              <span>
                {t(
                  'dashboardComponents.Console.CompanyManagement.addEdit.labels.Managers'
                )}
              </span>
            </div>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            {managersState?.length ? (
              <div className={classes.managersBlock}>
                {(managersState || []).map((mgr, idx) => (
                  <div
                    className={classes.managerItem}
                    key={`${mgr?.id || idx}`}
                  >
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager Name'
                        )}
                      </span>
                      <span className={classes.cellValue}>
                        {mgr?.firstName || '-'} {mgr?.lastName || '-'}{' '}
                        {mgr?.designation && (
                          <span>
                            <br />({mgr?.designation})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager Contact'
                        )}
                      </span>
                      <span
                        className={classNames(
                          classes.cellValue,
                          classes.managerContact
                        )}
                      >
                        {mgr?.email || '-'}
                        <br />
                        {mgr?.phone || '-'}
                      </span>
                    </div>
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Access'
                        )}
                      </span>
                      <span className={classes.cellValue}>
                        {mgr?.user ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Primary Contact'
                        )}
                      </span>
                      <span className={classes.cellValue}>
                        {mgr?.isPrimaryContact ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={classes.noManagers}>
                {t(
                  'dashboardComponents.Console.CompanyManagement.view.labels.No Managers'
                )}
              </div>
            )}
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.locationsHeader}>
              <span>
                {t(
                  'dashboardComponents.Console.CompanyManagement.addEdit.labels.Locations'
                )}
              </span>
            </div>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            {locations?.length ? (
              <div className={classes.locationsBlock}>
                {(locations || []).map((loc, idx) => (
                  <div
                    className={classes.locationItem}
                    key={`${loc?.name || idx}`}
                  >
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Location Name'
                        )}
                      </span>
                      <span className={classes.cellValue}>
                        {loc?.name || '-'}
                      </span>
                    </div>
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Address'
                        )}
                      </span>
                      <span className={classes.cellValue}>
                        {loc?.address || '-'}
                      </span>
                    </div>
                    <div className={classes.cell}>
                      <span className={classes.cellLabel}>
                        {t(
                          'dashboardComponents.Console.CompanyManagement.addEdit.labels.Country'
                        )}
                      </span>
                      <span className={classes.cellValue}>
                        {loc?.country || '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={classes.noLocations}>
                {t(
                  'dashboardComponents.Console.CompanyManagement.view.labels.No Locations'
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </SidePopup>
  );
}
